import { Button, Input, Select, Switch, Modal, Radio, Row, Col } from 'antd';
import { useEffect, useState } from 'react';
import { useLayerProperitesContext } from '../../../context/LayerPropertiesContext';
import MapPortalService from '../../../services/MapPortal/mapportal.service';
import CkEditor from '../../../shared/components/CkEditor';
import { useMutation, useQuery } from '@tanstack/react-query';
import './LayerProperties.scss';
import DOMPurify from 'dompurify';

const { Option } = Select;

const positions = ['topLeft', 'topRight', 'bottomLeft', 'bottomRight'];

//TODO: need to remove this static data when implementing admin module
const predefinedStyles = [
  {
    id: 'style1',
    name: 'Modern Blue',
    preview: {
      background: '#eaf3ff',
      color: '#1a4ed8',
      fontFamily: 'Arial',
    },
  },
  {
    id: 'style2',
    name: 'Minimal White',
    preview: {
      background: '#fff',
      color: '#333',
      fontFamily: 'Roboto',
    },
  },
  {
    id: 'style3',
    name: 'Dark Mode',
    preview: {
      background: '#1f1f1f',
      color: '#fff',
      fontFamily: 'Poppins',
    },
  },
];

const PopupsTab = () => {
  const { popupsEnabled, popupTitle, popupTitleUnit, defaultPosition, setState, uniqueLayerName, popupCustomStyle, popupStyleId, layerTitle } =
    useLayerProperitesContext();

  const { fetchFeaturesKeyAndValueMapping, fetchFeaturesFromLayer } = MapPortalService();
  const { data: mappingResponse } = useQuery(fetchFeaturesKeyAndValueMapping(uniqueLayerName));
  const { mutateAsync: fetchFeaturesAsync } = useMutation(fetchFeaturesFromLayer());
  const [unitOptions, setUnitOptions] = useState<{ label: string; value: string }[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState<string | null>(popupStyleId || null);
  const [popoverContent, setPopoverContent] = useState<any[]>([]);

  const feature = popoverContent?.[0];
  const featureProps = feature?.properties;
  const [customHtml, setCustomHtml] = useState(popupCustomStyle || '');

  useEffect(() => {
    if (!mappingResponse) return;
    const keys = Object.keys(mappingResponse).map((key) => ({
      label: key,
      value: key,
    }));
    setUnitOptions(keys);
  }, [mappingResponse]);

  useEffect(() => {
    const fetchFeatures = async () => {
      if (!uniqueLayerName) return;
      const data = await fetchFeaturesAsync({
        layerName: uniqueLayerName,
      });
      setPopoverContent(data?.features || []);

      const firstFeature = data?.features?.[0];
      const props = firstFeature?.properties || {};

      const defaultTemplate = `
        <div style="padding:10px;">
          <h4 style="text-align:center;">[title]</h4>

          <!-- IMAGE -->
          [__IMAGE__]

          <!-- VIDEO -->
          [__VIDEO__]

          <!-- FIELDS -->
          ${Object.keys(props)
            .filter((key) => !['photos', 'picture', 'video'].includes(key))
            .map((key) => `<p><strong>${key}:</strong> [${key}]</p>`)
            .join('')}
          <p><strong>id:</strong> [id]</p>
        </div>
      `;

      setCustomHtml((prev) => (prev.trim() ? prev : defaultTemplate));
    };

    fetchFeatures();
  }, [uniqueLayerName, fetchFeaturesAsync]);

  const handleSaveStyle = () => {
    setState((prev) => ({
      ...prev,
      popupCustomStyle: String(customHtml),
      popupStyleId: selectedStyle,
    }));
    setIsModalOpen(false);
  };

  // const selectedStyleObj = predefinedStyles.find(
  //   (style) => style.id === selectedStyle
  // );

  const replacePlaceholders = (html: string) => {
    if (!featureProps) return html;
    const titleValue =
      popupTitleUnit && featureProps?.[popupTitleUnit]
        ? `${popupTitle ? popupTitle + ' ' : ''}${featureProps[popupTitleUnit]}`
        : popupTitle || 'Feature Details';

    let replaced = html.replace(/\[title\]/g, titleValue).replace(/\[id\]/g, feature?.id || 'N/A');

    // IMAGE preview logic (matches JSX behavior)
    const imageSrc = featureProps?.photos || featureProps?.picture;
    const imageHTML = imageSrc ? `<img src="${imageSrc}" alt="Feature" class="popover__image" onerror="this.style.display='none'" />` : '';

    // VIDEO preview logic
    const videoHTML = featureProps?.video ? `<div class="popover__video">${featureProps.video}</div>` : '';

    replaced = replaced.replace(/\[__IMAGE__\]/g, imageHTML).replace(/\[__VIDEO__\]/g, videoHTML);

    // Replace rest of fields
    Object.entries(featureProps).forEach(([key, value]) => {
      if (!['photos', 'picture', 'video'].includes(key)) {
        replaced = replaced.replace(new RegExp(`\\[${key}\\]`, 'g'), String(value ?? ''));
      }
    });

    return DOMPurify.sanitize(replaced);
  };

  return (
    <>
      <h3 className="lp-tab__title--no-margin">{layerTitle}</h3>
      <div className="lp-controls__row">
        <span className="lp-controls__label">POP-UPS</span>
        <Switch checked={popupsEnabled} onChange={(checked) => setState((prev) => ({ ...prev, popupsEnabled: checked }))} />
      </div>
      {popupsEnabled && (
        <>
          <div className="lp-controls__row--no-margin">
            <span className="lp-controls__label--fixed">TITLE</span>
            <Input
              value={popupTitle}
              onChange={(e) => setState((prev) => ({ ...prev, popupTitle: e.target.value }))}
              className="lp-controls__flex lp-controls__flex--mr"
            />
            <Select
              value={popupTitleUnit}
              onChange={(value) => setState((prev) => ({ ...prev, popupTitleUnit: value }))}
              className="lp-controls__fixed"
              placeholder="Select Field"
              options={unitOptions}
              allowClear
            />
          </div>
          <div className="lp-controls__row--mt">
            <span className="lp-controls__label--fixed">DEFAULT POSITION</span>
            <Select
              value={defaultPosition}
              onChange={(value) => setState((prev) => ({ ...prev, defaultPosition: value }))}
              className="lp-controls__flex"
              placeholder="Select One"
            >
              {positions.map((position) => (
                <Option key={position} value={position}>
                  {position}
                </Option>
              ))}
            </Select>
          </div>
          <div className="lp-controls__row--mt">
            <span className="lp-controls__label--fixed">CONFIGURE POPUP</span>
            <Button type="primary" className="lp-popup__select-style-btn" title="Select Style" onClick={() => setIsModalOpen(true)}>
              Select Style
            </Button>
          </div>
        </>
      )}

      {/* Modal for Configure Pop-Up */}
      <Modal
        title="Configure Pop-Up"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={handleSaveStyle}
        width={1100}
        okText="Save Style"
      >
        <h4>Predefined Styles</h4>
        <Radio.Group value={selectedStyle} onChange={(e) => setSelectedStyle(e.target.value)} className="lp-popup__styles">
          {predefinedStyles.map((style) => (
            <Radio.Button key={style.id} value={style.id}>
              <div className={['lp-popup__style-card', `lp-popup__style-card--${style.id}`].join(' ')}>{style.name}</div>
            </Radio.Button>
          ))}
        </Radio.Group>

        <h4>Customize Content</h4>
        <Row gutter={16}>
          <Col span={12}>
            <div className="lp-popup__editor-box">
              {/* TODO: need to allow to upload image and video */}
              <CkEditor
                id="customizedContent"
                label="Customize Pop-up Content"
                value={
                  customHtml ||
                  `
                    <div style="padding:10px;">
                    <h4 style="text-align:center;">[title]</h4>

                    <!-- IMAGE -->
                    [__IMAGE__]

                    <!-- VIDEO -->
                    [__VIDEO__]

                    <!-- FIELDS -->
                    ${(featureProps ? Object.keys(featureProps) : [])
                      .filter((key) => !['photos', 'picture', 'video'].includes(key))
                      .map((key) => `<p><strong>${key}:</strong> [${key}]</p>`)
                      .join('')}
                    <p><strong>id:</strong> [id]</p>
                    </div>
                  `
                }
                onChange={(val) => setCustomHtml(val)}
                uploadFeature
              />
            </div>
          </Col>

          <Col span={12}>
            <div
              id="custom-preview-container"
              className={['lp-popup__preview-box', selectedStyle ? `lp-popup__preview-box--${selectedStyle}` : ''].filter(Boolean).join(' ')}
            >
              <div
                dangerouslySetInnerHTML={{
                  __html: replacePlaceholders(customHtml),
                }}
              />
            </div>
          </Col>
        </Row>
      </Modal>
    </>
  );
};

export default PopupsTab;
